import { useState, useEffect } from 'react'
import {
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  getExpandedRowModel,
  useReactTable,
  ExpandedState,
  Row,
  ColumnDef,
} from '@tanstack/react-table'
import { MessageSquare } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { DataTablePagination } from './data-table-pagination'
import { 
  useTicketsTableColumns, 
  renderTableHeader, 
  renderTableRow, 
  renderNoResults 
} from './tickets-table-columns'
import { ChatDrawer } from './chat-drawer'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { useUpdateTicketMutation, useUploadEvidenceMutation } from '../hooks/use-tickets-query'
import { tokens } from '@/locales/translations/tokens'
import { toast } from '@/hooks/use-toast'

interface TicketsTableProps {
  tickets: Ticket[]
}

export function TicketsTable({ tickets: initialTickets }: TicketsTableProps) {
  const { t } = useTranslation()
  const [tickets, setTickets] = useState<Ticket[]>(initialTickets)
  const [expanded, setExpanded] = useState<ExpandedState>({})
  const [selectedTicketForChat, setSelectedTicketForChat] =
    useState<Ticket | null>(null)
  const [isChatOpen, setIsChatOpen] = useState(false)
  const [showCloseDialog, setShowCloseDialog] = useState(false)
  const [ticketToClose, setTicketToClose] = useState<string | null>(null)
  const updateTicketMutation = useUpdateTicketMutation()
  const uploadEvidenceMutation = useUploadEvidenceMutation()

  useEffect(() => {
    setTickets(initialTickets)
  }, [initialTickets])

  const handleChatClick = (ticket: Ticket, e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setSelectedTicketForChat(ticket)
    setIsChatOpen(true)
  }

  const handleTicketUpdate = (updatedTicket: Ticket) => {
    setTickets(prevTickets => 
      prevTickets.map(ticket => 
        ticket.external_id === updatedTicket.external_id ? updatedTicket : ticket
      )
    )
    
    if (selectedTicketForChat?.external_id === updatedTicket.external_id) {
      setSelectedTicketForChat(updatedTicket)
    }
  }

  const handleCloseTicket = (ticketId: string) => {
    setTicketToClose(ticketId)
    setShowCloseDialog(true)
  }

  const handleUploadEvidence = async (ticketId: string, file: File) => {
    try {
      const result = await uploadEvidenceMutation.mutateAsync({ file, ticketId })
      
      await updateTicketMutation.mutateAsync({
        data: { attachments: [result.attachment] },
        externalId: ticketId
      })
      
      setTickets(prevTickets => 
        prevTickets.map(ticket => 
          ticket.external_id === ticketId 
            ? { ...ticket, attachments: [...ticket.attachments, result.attachment] }
            : ticket
        )
      )

      toast({
        title: 'Sended evidence',
        description: 'The evidence has been sent to the ticket',
      })
    } catch (_error) {
      // Erro tratado pela mutation
    }
  }

  const confirmCloseTicket = async () => {
    if (!ticketToClose) return

    try {
      await updateTicketMutation.mutateAsync({
        data: { status: 'resolved' },
        externalId: ticketToClose
      })

      setTickets(prevTickets => 
        prevTickets.map(ticket => 
          ticket.external_id === ticketToClose 
            ? { ...ticket, status: 'resolved' }
            : ticket
        )
      )

      setShowCloseDialog(false)
      setTicketToClose(null)
    } catch (_error) {
      // Erro silencioso - o usuário pode tentar novamente
    }
  }

  const columns = useTicketsTableColumns(handleChatClick) as ColumnDef<Ticket>[]

  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})

  const table = useReactTable({
    data: tickets,
    columns,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      expanded,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onExpandedChange: setExpanded,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
  })

  const handleRowExpand = (row: Row<Ticket>) => {
    const isCurrentlyExpanded = row.getIsExpanded()

    if (isCurrentlyExpanded) {
      row.toggleExpanded()
    } else {
      const allRows = table.getRowModel().rows
      allRows.forEach((r) => {
        if (r.id !== row.id && r.getIsExpanded()) {
          r.toggleExpanded()
        }
      })
      row.toggleExpanded()
    }
  }

  if (tickets.length === 0) {
    return (
      <div className='flex flex-col items-center justify-center py-16 text-center'>
        <div className='mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-gray-100'>
          <MessageSquare className='h-10 w-10 text-gray-400' />
        </div>
        <h3 className='mb-2 text-lg font-semibold text-gray-900'>
          {t(tokens.tickets.empty.title)}
        </h3>
        <p className='max-w-md text-gray-500'>
          {t(tokens.tickets.empty.description)}
        </p>
      </div>
    )
  }

  return (
    <div className='space-y-4'>
      <div className='rounded-md border'>
        <div className='overflow-x-auto'>
          <div className='inline-block min-w-full align-middle'>
            <div className='overflow-hidden'>
              {renderTableHeader(t)}

              <div className='max-h-[600px] overflow-y-auto'>
                {table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => 
                    renderTableRow(row, handleRowExpand, handleChatClick, handleCloseTicket, handleUploadEvidence, updateTicketMutation.isPending, uploadEvidenceMutation.isPending, t)
                  )
                ) : (
                  renderNoResults(t)
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <DataTablePagination table={table} />

      {selectedTicketForChat && (
        <ChatDrawer
          isOpen={isChatOpen}
          onClose={() => {
            setIsChatOpen(false)
            setSelectedTicketForChat(null)
          }}
          ticket={selectedTicketForChat}
          onTicketUpdate={handleTicketUpdate}
        />
      )}

      <ConfirmDialog
        open={showCloseDialog}
        onOpenChange={setShowCloseDialog}
        title={t(tokens.tickets.close.dialog_title)}
        desc={t(tokens.tickets.close.dialog_description)}
        confirmText={t(tokens.tickets.close.dialog_confirm)}
        destructive
        handleConfirm={confirmCloseTicket}
        isLoading={updateTicketMutation.isPending}
      />
    </div>
  )
}
